import { useState, useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { useServicos, useCreateServico, useUpdateServico, useDeleteServico, useUpsertPlano, useDeletePlano, useUpsertAdicional, useDeleteAdicional } from '@/hooks/useServicos'
import { useIntegracoes } from '@/hooks/useIntegracoes'
import { formatCurrency } from '@/lib/formatters'
import {
    Search, Plus, Edit, Trash2, X, Loader2, Package, Shield, Clock, DollarSign, CheckCircle2, Globe, Link, Copy, Check, Split, CreditCard, Star, Sparkles, Image as ImageIcon, Heart, Layers, ListPlus, ShoppingBag, Percent, Users, ChevronDown, ChevronUp, GripVertical
} from 'lucide-react'
import type { Servico, CheckoutBranding, SplitRegra, FormaPagamento, ServicoPlano, ServicoAdicional, CrossSellConfig, CrossSellItem } from '@/types/database'
import { useAllParceiros } from '@/hooks/useParceiros'
import { useAllServicos } from '@/hooks/useServicos'

export function ServicosPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [search, setSearch] = useState('')
    const [showForm, setShowForm] = useState(false)
    const [editing, setEditing] = useState<Servico | null>(null)

    useEffect(() => { setPageTitle('Serviços') }, [setPageTitle])

    const { data: result, isLoading } = useServicos({ search })
    const { data: integracoes } = useIntegracoes()
    const { data: parceiros } = useAllParceiros()
    const createMut = useCreateServico()
    const updateMut = useUpdateServico()
    const deleteMut = useDeleteServico()

    const servicos = result?.data || []

    const handleSave = async (input: Partial<Servico>) => {
        if (editing) {
            await updateMut.mutateAsync({ ...input, id: editing.id })
        } else {
            await createMut.mutateAsync(input)
        }
        setShowForm(false)
        setEditing(null)
    }

    const handleEdit = (s: Servico) => {
        setEditing(s)
        setShowForm(true)
    }

    const handleDelete = async (id: string) => {
        if (confirm('Tem certeza que deseja desativar este serviço?')) {
            await deleteMut.mutateAsync(id)
        }
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-surface-900">Serviços</h2>
                    <p className="text-sm text-surface-700/60 mt-0.5">Planos e serviços oferecidos pela associação</p>
                </div>
                <button onClick={() => { setEditing(null); setShowForm(true) }} className="btn btn-primary btn-md gap-2">
                    <Plus size={18} /> Novo Serviço
                </button>
            </div>

            {/* Search */}
            <div className="glass-card p-4">
                <div className="relative">
                    <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-700/40" />
                    <input type="text" value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar serviços..."
                        className="input pl-10" />
                </div>
            </div>

            {/* Service Cards Grid */}
            {isLoading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 size={24} className="animate-spin text-primary-600" />
                </div>
            ) : servicos.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                    <Package size={40} className="text-surface-700/20 mb-3" />
                    <p className="text-surface-700/60">Nenhum serviço encontrado</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {servicos.map((s) => (
                        <div key={s.id} className="glass-card p-6 flex flex-col group hover:shadow-lg transition-shadow">
                            <div className="flex items-start justify-between mb-4">
                                <div className="w-12 h-12 rounded-2xl bg-primary-100 flex items-center justify-center overflow-hidden">
                                     {s.imagem_url ? (
                                         <img src={s.imagem_url} alt={s.nome} className="w-full h-full object-cover" />
                                     ) : (
                                         <Shield size={24} className="text-primary-600" />
                                     )}
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleEdit(s)}
                                        className="p-1.5 rounded-lg hover:bg-primary-50 text-surface-700/60 hover:text-primary-600">
                                        <Edit size={15} />
                                    </button>
                                    <button onClick={() => handleDelete(s.id)}
                                        className="p-1.5 rounded-lg hover:bg-danger-50 text-surface-700/60 hover:text-danger-600">
                                        <Trash2 size={15} />
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-lg font-semibold text-surface-900 truncate">{s.nome}</h3>
                                {s.destaque_loja && (
                                    <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                                        <Star size={10} fill="currentColor" /> Popular
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-surface-700/60 mb-4 flex-1 line-clamp-2">{s.descricao || 'Sem descrição'}</p>

                            <div className="space-y-2 pt-4 border-t border-surface-100">
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center gap-1.5 text-sm text-surface-700/60">
                                        <DollarSign size={14} /> Mensalidade
                                    </span>
                                    <span className="font-bold text-primary-600">{formatCurrency(s.valor_mensal)}</span>
                                </div>
                                
                                <div className="flex items-center justify-between">
                                    <span className="flex items-center gap-1.5 text-sm text-surface-700/60">
                                        <CheckCircle2 size={14} /> Loja do Portal
                                    </span>
                                    <span className={`badge ${s.visivel_portal ? 'badge-success' : 'badge-danger'}`}>
                                        {s.visivel_portal ? 'Visível' : 'Oculto'}
                                    </span>
                                </div>

                                {/* Planos, Adicionais, Cross-sell badges */}
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                    {(s.planos?.filter(p => p.ativo)?.length || 0) > 0 && (
                                        <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 text-[10px] font-bold flex items-center gap-1">
                                            <Layers size={10} /> {s.planos!.filter(p => p.ativo).length} planos
                                        </span>
                                    )}
                                    {(s.adicionais?.filter(a => a.ativo)?.length || 0) > 0 && (
                                        <span className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-600 text-[10px] font-bold flex items-center gap-1">
                                            <ListPlus size={10} /> {s.adicionais!.filter(a => a.ativo).length} adicionais
                                        </span>
                                    )}
                                    {s.config_contratacao?.cross_sell?.habilitado && (
                                        <span className="px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-600 text-[10px] font-bold flex items-center gap-1">
                                            <ShoppingBag size={10} /> Cross-sell
                                        </span>
                                    )}
                                    {!!s.pontos_adesao && s.pontos_adesao > 0 && (
                                        <span className="px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 text-[10px] font-bold flex items-center gap-1">
                                            <Heart size={10} className="fill-current" /> +{s.pontos_adesao}
                                        </span>
                                    )}
                                </div>

                                {s.contratacao_online && (
                                    <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-surface-100">
                                        <Globe size={14} className="text-blue-500" />
                                        <span className="text-xs font-medium text-blue-600">Contratação online</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {showForm && (
                <ServiceFormModal
                    service={editing}
                    integracoes={integracoes || []}
                    parceiros={parceiros || []}
                    onSave={handleSave}
                    onClose={() => { setShowForm(false); setEditing(null) }}
                    isPending={createMut.isPending || updateMut.isPending}
                />
            )}
        </div>
    )
}

type FormTab = 'geral' | 'planos' | 'adicionais' | 'crosssell'

function ServiceFormModal({ service, integracoes, parceiros, onSave, onClose, isPending }: {
    service: Servico | null
    integracoes: { id: string, nome: string }[]
    parceiros: { id: string, nome: string, wallet_id: string }[]
    onSave: (input: Partial<Servico>) => Promise<void>
    onClose: () => void
    isPending: boolean
}) {
    const [activeTab, setActiveTab] = useState<FormTab>('geral')
    const { data: allServicos } = useAllServicos()
    const [nome, setNome] = useState(service?.nome || '')
    const [descricao, setDescricao] = useState(service?.descricao || '')
    const [codigo, setCodigo] = useState(service?.codigo || '')
    const [valorMensal, setValorMensal] = useState(String(service?.valor_mensal || ''))
    const [valorMensalAssociado, setValorMensalAssociado] = useState(String(service?.valor_mensal_associado || ''))
    const [valorMensalNaoAssociado, setValorMensalNaoAssociado] = useState(String(service?.valor_mensal_nao_associado || ''))
    const [carenciaDias, setCarenciaDias] = useState(String(service?.carencia_dias || '0'))
    const [exigeVeiculo, setExigeVeiculo] = useState(service?.exige_veiculo ?? true)
    const [contratacaoOnline, setContratacaoOnline] = useState(service?.contratacao_online ?? false)
    
    // Novas Configurações da Loja
    const [imagemUrl, setImagemUrl] = useState(service?.imagem_url || '')
    const [destaqueLoja, setDestaqueLoja] = useState(service?.destaque_loja ?? false)
    const [visivelPortal, setVisivelPortal] = useState(service?.visivel_portal ?? true)
    const [detalhesLoja, setDetalhesLoja] = useState(service?.detalhes_loja || '')
    const [textoBotao, setTextoBotao] = useState(service?.texto_botao || 'Saiba Mais')

    /* Forma de pagamento */
    const [formaPagamento, setFormaPagamento] = useState<FormaPagamento>(service?.forma_pagamento || 'UNDEFINED')

    /* Split config */
    const [splitHabilitado, setSplitHabilitado] = useState(service?.split_habilitado ?? false)
    const [splitRegras, setSplitRegras] = useState<SplitRegra[]>(service?.split_config?.regras || [])

    /* Config contratação */
    const cfg = service?.config_contratacao
    const [termoAceite, setTermoAceite] = useState(cfg?.termo_aceite || '')
    const [gerarAdesao, setGerarAdesao] = useState(cfg?.gerar_adesao ?? false)
    const [adesaoProRata, setAdesaoProRata] = useState(cfg?.adesao_pro_rata ?? false)
    const [valorAdesaoFixo, setValorAdesaoFixo] = useState(String(cfg?.valor_adesao_fixo || ''))
    const [valorAdesaoFixoNaoAssociado, setValorAdesaoFixoNaoAssociado] = useState(String(cfg?.valor_adesao_fixo_nao_associado || ''))
    const [ativacaoViaApi, setAtivacaoViaApi] = useState(cfg?.ativacao_via_api ?? false)
    const [integracaoId, setIntegracaoId] = useState(cfg?.integracao_id || '')
    const [destaque, setDestaque] = useState(cfg?.destaque ?? false)
    const [pontosAdesao, setPontosAdesao] = useState(String(service?.pontos_adesao || ''))
    const [pontosMultiplicador, setPontosMultiplicador] = useState(String(service?.pontos_multiplicador || ''))

    /* Checkout branding */
    const brd = service?.config?.checkout_branding as CheckoutBranding | undefined
    const [corPrimaria, setCorPrimaria] = useState(brd?.cor_primaria || '#0ea5e9')
    const [corFundo, setCorFundo] = useState(brd?.cor_fundo || '#0f172a')
    const [logoUrl, setLogoUrl] = useState(brd?.logo_url || '')
    const [logoContorno, setLogoContorno] = useState(brd?.logo_contorno ?? false)
    const [tituloHero, setTituloHero] = useState(brd?.titulo_hero || '')
    const [manualUrl, setManualUrl] = useState(service?.manual_url || '')
    const [linkCopied, setLinkCopied] = useState(false)

    /* Cross-sell state */
    const existingCrossSell = service?.config_contratacao?.cross_sell
    const [crossSellState, setCrossSellState] = useState<CrossSellConfig>(existingCrossSell || { habilitado: false, servicos: [] })

    const checkoutLink = service ? `${window.location.origin}/checkout/${service.id}` : null

    const copyLink = () => {
        if (!checkoutLink) return
        navigator.clipboard.writeText(checkoutLink)
        setLinkCopied(true)
        setTimeout(() => setLinkCopied(false), 2000)
    }

    const addSplitRegra = () => {
        setSplitRegras([...splitRegras, {
            parceiro_id: '',
            wallet_id: '',
            tipo_valor: 'percentualValue',
            valor: 0,
            aplicar_em: 'todas',
        }])
    }

    const updateSplitRegra = (index: number, updates: Partial<SplitRegra>) => {
        const newRegras = [...splitRegras]
        newRegras[index] = { ...newRegras[index]!, ...updates }
        setSplitRegras(newRegras)
    }

    const removeSplitRegra = (index: number) => {
        setSplitRegras(splitRegras.filter((_, i) => i !== index))
    }

    const handleParceiroChange = (index: number, parceiroId: string) => {
        const p = parceiros.find(x => x.id === parceiroId)
        updateSplitRegra(index, {
            parceiro_id: parceiroId,
            wallet_id: p?.wallet_id || '',
        })
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        await onSave({
            nome,
            descricao: descricao || null,
            codigo: codigo.trim().toUpperCase() || null,
            valor_mensal: parseFloat(valorMensal),
            valor_mensal_associado: valorMensalAssociado ? parseFloat(valorMensalAssociado) : null,
            valor_mensal_nao_associado: valorMensalNaoAssociado ? parseFloat(valorMensalNaoAssociado) : null,
            carencia_dias: parseInt(carenciaDias),
            exige_veiculo: exigeVeiculo,
            contratacao_online: contratacaoOnline,
            forma_pagamento: formaPagamento,
            split_habilitado: splitHabilitado,
            split_config: { regras: splitHabilitado ? splitRegras : [] },
            config_contratacao: {
                termo_aceite: termoAceite.trim() || null,
                gerar_adesao: gerarAdesao,
                adesao_pro_rata: adesaoProRata,
                valor_adesao_fixo: !adesaoProRata && gerarAdesao ? parseFloat(valorAdesaoFixo) || null : null,
                valor_adesao_fixo_nao_associado: !adesaoProRata && gerarAdesao ? parseFloat(valorAdesaoFixoNaoAssociado) || null : null,
                ativacao_via_api: ativacaoViaApi,
                integracao_id: ativacaoViaApi ? (integracaoId || null) : null,
                destaque,
                cross_sell: crossSellState.habilitado ? crossSellState : null,
            },
            pontos_adesao: pontosAdesao ? parseInt(pontosAdesao) : null,
            pontos_multiplicador: pontosMultiplicador ? parseFloat(pontosMultiplicador) : null,
            config: {
                checkout_branding: contratacaoOnline ? {
                    cor_primaria: corPrimaria,
                    cor_fundo: corFundo,
                    logo_url: logoUrl.trim() || null,
                    logo_contorno: logoContorno,
                    titulo_hero: tituloHero.trim() || null,
                } : undefined,
            },
            manual_url: manualUrl.trim() || null,
            imagem_url: imagemUrl.trim() || null,
            destaque_loja: destaqueLoja,
            visivel_portal: visivelPortal,
            detalhes_loja: detalhesLoja.trim() || null,
            texto_botao: textoBotao.trim() || 'Saiba Mais',
        })
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in py-4">
            <div className="bg-white p-6 w-full max-w-2xl mx-4 max-h-full overflow-y-auto rounded-3xl shadow-elevated custom-scrollbar">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold">{service ? 'Editar Serviço' : 'Novo Serviço'}</h3>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100"><X size={18} /></button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-surface-700 mb-1.5">Nome</label>
                            <input type="text" value={nome} onChange={(e) => setNome(e.target.value)}
                                required placeholder="Ex: Proteção Completa" className="input" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-surface-700 mb-1.5">Código (Sistema)</label>
                            <input type="text" value={codigo} onChange={(e) => setCodigo(e.target.value)}
                                placeholder="PROT_01" className="input uppercase" />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-surface-700 mb-1.5">Descrição Curta</label>
                        <textarea value={descricao} onChange={(e) => setDescricao(e.target.value)}
                            rows={2} placeholder="Descreva os benefícios do serviço..."
                            className="input resize-none" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-surface-100">
                            <label className="flex items-center gap-3 cursor-pointer flex-1">
                                <input type="checkbox" checked={contratacaoOnline}
                                    onChange={(e) => setContratacaoOnline(e.target.checked)}
                                    className="w-4 h-4 rounded border-surface-300 text-primary-600 focus:ring-primary-500" />
                                <div>
                                    <p className="text-sm font-medium text-surface-900">Contratação online</p>
                                    <p className="text-[10px] text-surface-700/60 uppercase font-bold tracking-wider">Disponível em checkout</p>
                                </div>
                            </label>
                            <Globe size={18} className={contratacaoOnline ? 'text-blue-500' : 'text-surface-300'} />
                        </div>

                        <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-surface-100">
                            <label className="flex items-center gap-3 cursor-pointer flex-1">
                                <input type="checkbox" checked={exigeVeiculo}
                                    onChange={(e) => setExigeVeiculo(e.target.checked)}
                                    className="w-4 h-4 rounded border-surface-300 text-amber-600 focus:ring-amber-500" />
                                <div>
                                    <p className="text-sm font-medium text-surface-900">Exige veículo</p>
                                    <p className="text-[10px] text-surface-700/60 uppercase font-bold tracking-wider">Pede placa no checkout</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Dashboard/Shop Details */}
                    <div className="space-y-4 p-4 rounded-2xl bg-emerald-50/40 border border-emerald-100">
                        <div className="flex items-center gap-2">
                             <Package size={16} className="text-emerald-600" />
                             <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Exibição na Loja (Portal)</p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <label className="flex items-center gap-2 cursor-pointer p-2 bg-white rounded-xl border border-emerald-100">
                                <input type="checkbox" checked={visivelPortal}
                                    onChange={(e) => setVisivelPortal(e.target.checked)}
                                    className="w-4 h-4 rounded border-emerald-300 text-emerald-600" />
                                <span className="text-xs font-bold text-surface-700">Visível</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer p-2 bg-white rounded-xl border border-emerald-100">
                                <input type="checkbox" checked={destaqueLoja}
                                    onChange={(e) => setDestaqueLoja(e.target.checked)}
                                    className="w-4 h-4 rounded border-emerald-300 text-emerald-600" />
                                <span className="text-xs font-bold text-surface-700">Popular</span>
                            </label>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-surface-500 uppercase tracking-widest mb-1 ml-1">Imagem (URL)</label>
                                <div className="relative">
                                    <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-300" size={16} />
                                    <input type="url" value={imagemUrl} onChange={(e) => setImagemUrl(e.target.value)}
                                        placeholder="https://..." className="input text-sm pl-10" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-surface-500 uppercase tracking-widest mb-1 ml-1">Texto do Botão</label>
                                <input type="text" value={textoBotao} onChange={(e) => setTextoBotao(e.target.value)}
                                    placeholder="Saiba Mais" className="input text-sm" />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-surface-500 uppercase tracking-widest mb-1 ml-1">Detalhes (Descrição Longa)</label>
                            <textarea value={detalhesLoja} onChange={(e) => setDetalhesLoja(e.target.value)}
                                rows={3} placeholder="Conteúdo que aparece ao clicar em Saiba Mais..."
                                className="input resize-none text-xs" />
                        </div>
                    </div>

                    {/* Pricing section */}
                    <div className="space-y-3 p-4 rounded-xl bg-surface-50 border border-surface-100">
                        <p className="text-xs font-semibold text-surface-700 uppercase tracking-wide">Preços</p>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-surface-700 mb-1">Valor Base (R$)</label>
                                <input type="number" step="0.01" min="0" value={valorMensal}
                                    onChange={(e) => setValorMensal(e.target.value)}
                                    required placeholder="99.90" className="input text-sm" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-surface-700 mb-1">Carência (dias)</label>
                                <input type="number" min="0" value={carenciaDias}
                                    onChange={(e) => setCarenciaDias(e.target.value)}
                                    required placeholder="90" className="input text-sm" />
                            </div>
                        </div>
                    </div>

                    {/* ===== MULTIPLANOS (TIERS) ===== */}
                    {service && (
                        <PlanosManager servicoId={service.id} planos={service.planos?.filter(p => p.ativo) || []} />
                    )}

                    {/* ===== ADICIONAIS (ADD-ONS) ===== */}
                    {service && (
                        <AdicionaisManager servicoId={service.id} adicionais={service.adicionais?.filter(a => a.ativo) || []} />
                    )}

                    {/* ===== CONFIGURAÇÕES DE CONTRATAÇÃO ===== */}
                    {contratacaoOnline && (
                        <div className="space-y-4 p-4 rounded-2xl bg-blue-50/40 border border-blue-100">
                            <div className="flex items-center gap-2">
                                <CreditCard size={16} className="text-blue-600" />
                                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Configurações de Contratação</p>
                            </div>

                            {/* Adesão */}
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input type="checkbox" checked={gerarAdesao}
                                    onChange={(e) => setGerarAdesao(e.target.checked)}
                                    className="w-4 h-4 rounded border-surface-300 text-primary-600" />
                                <div>
                                    <p className="text-sm font-medium text-surface-900">Gerar taxa de adesão</p>
                                    <p className="text-xs text-surface-700/60">Cobra uma taxa única ao contratar</p>
                                </div>
                            </label>

                            {gerarAdesao && (
                                <div className="space-y-3 pl-7">
                                    <label className="flex items-center gap-3 cursor-pointer">
                                        <input type="checkbox" checked={adesaoProRata}
                                            onChange={(e) => setAdesaoProRata(e.target.checked)}
                                            className="w-4 h-4 rounded border-surface-300 text-primary-600" />
                                        <span className="text-sm text-surface-700">Calcular pro-rata (proporcional ao mês)</span>
                                    </label>
                                    {!adesaoProRata && (
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium text-surface-700 mb-1">Adesão Associado (R$)</label>
                                                <input type="number" step="0.01" min="0" value={valorAdesaoFixo}
                                                    onChange={(e) => setValorAdesaoFixo(e.target.value)}
                                                    placeholder="0.00" className="input text-sm" />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-surface-700 mb-1">Adesão Não Assoc. (R$)</label>
                                                <input type="number" step="0.01" min="0" value={valorAdesaoFixoNaoAssociado}
                                                    onChange={(e) => setValorAdesaoFixoNaoAssociado(e.target.value)}
                                                    placeholder="0.00" className="input text-sm" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Termo de Aceite */}
                            <div>
                                <label className="block text-xs font-medium text-surface-700 mb-1">Termo de Aceite</label>
                                <textarea value={termoAceite} onChange={(e) => setTermoAceite(e.target.value)}
                                    rows={4} placeholder="Cole aqui o texto completo do termo de aceite..."
                                    className="input resize-none text-xs font-mono" />
                                <p className="text-xs text-surface-700/40 mt-1">O associado deverá ler e aceitar este termo antes de contratar.</p>
                            </div>

                            {/* Ativação via API */}
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input type="checkbox" checked={ativacaoViaApi}
                                    onChange={(e) => setAtivacaoViaApi(e.target.checked)}
                                    className="w-4 h-4 rounded border-surface-300 text-primary-600" />
                                <div>
                                    <p className="text-sm font-medium text-surface-900">Ativação via API</p>
                                    <p className="text-xs text-surface-700/60">Dispara uma integração ao confirmar pagamento</p>
                                </div>
                            </label>

                            {ativacaoViaApi && integracoes.length > 0 && (
                                <div className="pl-7">
                                    <label className="block text-xs font-medium text-surface-700 mb-1">Integração</label>
                                    <select value={integracaoId} onChange={(e) => setIntegracaoId(e.target.value)} className="input text-sm">
                                        <option value="">Selecione...</option>
                                        {integracoes.map(i => (
                                            <option key={i.id} value={i.id}>{i.nome}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-medium text-surface-700 mb-1">Dia de vencimento padrão</label>
                                <select className="input text-sm" value="10" disabled>
                                    <option value="10">Dia 10 (Somente dia 10 e 20 disponíveis no checkout)</option>
                                </select>
                            </div>
                        </div>
                    )}

                    {/* ===== CROSS-SELL ===== */}
                    {contratacaoOnline && (
                        <CrossSellSection
                            crossSell={service?.config_contratacao?.cross_sell || null}
                            allServicos={(allServicos || []).filter(s => s.id !== service?.id)}
                            onChange={(cs) => {
                                // stored via config_contratacao in handleSubmit
                            }}
                            crossSellState={crossSellState}
                            setCrossSellState={setCrossSellState}
                        />
                    )}

                    {/* ===== GAMIFICAÇÃO (SEMPRE JUNTOS) ===== */}
                    <div className="space-y-3 p-4 rounded-2xl bg-rose-50/40 border border-rose-100">
                        <div className="flex items-center gap-2">
                            <Heart size={16} className="text-rose-600" />
                            <p className="text-xs font-semibold text-rose-700 uppercase tracking-wide">Programa Sempre Juntos</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-surface-700 mb-1">Pontos na Adesão</label>
                                <input type="number" min="0" value={pontosAdesao}
                                    onChange={(e) => setPontosAdesao(e.target.value)}
                                    placeholder="Ex: 50" className="input text-sm" />
                                <p className="text-[10px] text-surface-400 mt-1">Pontos creditados automaticamente no check-out.</p>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-surface-700 mb-1">Multiplicador de Check-in</label>
                                <input type="number" step="0.1" min="1" value={pontosMultiplicador}
                                    onChange={(e) => setPontosMultiplicador(e.target.value)}
                                    placeholder="Ex: 1.5" className="input text-sm" />
                                <p className="text-[10px] text-surface-400 mt-1">Opcional. Ex: 1.5 aumenta em 50% os pontos do sócio.</p>
                            </div>
                        </div>
                    </div>

                    {/* ===== SPLIT DE RECEITA ===== */}
                    <div className="space-y-3 p-4 rounded-2xl bg-purple-50/40 border border-purple-100">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Split size={16} className="text-purple-600" />
                                <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Split de Receita</p>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={splitHabilitado}
                                    onChange={(e) => setSplitHabilitado(e.target.checked)}
                                    className="w-4 h-4 rounded border-surface-300 text-purple-600" />
                                <span className="text-xs text-surface-700">Ativar</span>
                            </label>
                        </div>

                        {splitHabilitado && (
                            <div className="space-y-3">
                                {splitRegras.map((regra, index) => (
                                    <div key={index} className="p-3 rounded-xl bg-white border border-purple-100 space-y-2">
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs font-medium text-purple-700">Regra {index + 1}</span>
                                            <button type="button" onClick={() => removeSplitRegra(index)}
                                                className="p-1 rounded hover:bg-danger-50 text-danger-600">
                                                <X size={12} />
                                            </button>
                                        </div>
                                        <select value={regra.parceiro_id}
                                            onChange={(e) => handleParceiroChange(index, e.target.value)}
                                            className="input text-sm">
                                            <option value="">Selecione o Parceiro...</option>
                                            {parceiros.map(p => (
                                                <option key={p.id} value={p.id}>{p.nome}</option>
                                            ))}
                                        </select>
                                        <div className="grid grid-cols-2 gap-2">
                                            <select value={regra.tipo_valor}
                                                onChange={(e) => updateSplitRegra(index, { tipo_valor: e.target.value as any })}
                                                className="input text-sm">
                                                <option value="percentualValue">Percentual (%)</option>
                                                <option value="fixedValue">Valor Fixo (R$)</option>
                                            </select>
                                            <input type="number" step="0.01" min="0" value={regra.valor}
                                                onChange={(e) => updateSplitRegra(index, { valor: parseFloat(e.target.value) || 0 })}
                                                placeholder={regra.tipo_valor === 'percentualValue' ? '10' : '5.00'}
                                                className="input text-sm" />
                                        </div>
                                        <div>
                                            <label className="block text-[11px] font-medium text-surface-600 mb-0.5">Aplicar em</label>
                                            <select value={regra.aplicar_em}
                                                onChange={(e) => updateSplitRegra(index, { aplicar_em: e.target.value as any })}
                                                className="input text-xs">
                                                <option value="todas">Todas as cobranças</option>
                                                <option value="exceto_primeira">Todas exceto a primeira</option>
                                                <option value="adesao">Somente adesão</option>
                                                <option value="primeira">Somente 1ª mensalidade</option>
                                                <option value="segunda">Somente 2ª mensalidade</option>
                                                <option value="especificas">Cobranças específicas</option>
                                            </select>
                                        </div>
                                        {regra.aplicar_em === 'especificas' && (
                                            <div>
                                                <label className="block text-[11px] font-medium text-surface-600 mb-0.5">Números das cobranças (separados por vírgula)</label>
                                                <input type="text"
                                                    value={(regra.cobrancas_especificas || []).join(', ')}
                                                    onChange={(e) => {
                                                        const nums = e.target.value.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n))
                                                        updateSplitRegra(index, { cobrancas_especificas: nums })
                                                    }}
                                                    className="input text-xs" placeholder="1, 2, 3" />
                                                <p className="text-[10px] text-surface-400 mt-0.5">Ex: 1, 2, 3 — split nas 3 primeiras cobranças</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                                <button type="button" onClick={addSplitRegra}
                                    className="w-full py-2 rounded-lg border-2 border-dashed border-orange-200 text-orange-600 text-xs font-medium hover:bg-orange-50 transition-colors flex items-center justify-center gap-1.5">
                                    <Plus size={14} /> Adicionar Regra de Split
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ===== CHECKOUT BRANDING ===== */}
                    {contratacaoOnline && (
                        <div className="space-y-3 p-4 rounded-2xl bg-surface-50 border border-surface-200">
                            <div className="flex items-center gap-2">
                                <Sparkles size={16} className="text-surface-500" />
                                <p className="text-xs font-semibold text-surface-600 uppercase tracking-wide">Visual do Checkout</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-surface-700 mb-1">Cor Primária</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" value={corPrimaria} onChange={(e) => setCorPrimaria(e.target.value)}
                                            className="w-8 h-8 rounded cursor-pointer border-0" />
                                        <input type="text" value={corPrimaria} onChange={(e) => setCorPrimaria(e.target.value)}
                                            className="input text-xs flex-1" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-surface-700 mb-1">Cor de Fundo</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" value={corFundo} onChange={(e) => setCorFundo(e.target.value)}
                                            className="w-8 h-8 rounded cursor-pointer border-0" />
                                        <input type="text" value={corFundo} onChange={(e) => setCorFundo(e.target.value)}
                                            className="input text-xs flex-1" />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-surface-700 mb-1">URL do Logo</label>
                                <input type="url" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)}
                                    placeholder="https://..." className="input text-sm" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-surface-700 mb-1">Título Hero</label>
                                <input type="text" value={tituloHero} onChange={(e) => setTituloHero(e.target.value)}
                                    placeholder="Ex: Proteção que você merece" className="input text-sm" />
                            </div>
                            {service && checkoutLink && (
                                <div>
                                    <label className="block text-xs font-medium text-surface-700 mb-1">Link do Checkout</label>
                                    <div className="flex gap-2">
                                        <input readOnly value={checkoutLink} className="input text-xs flex-1 bg-surface-50" />
                                        <button type="button" onClick={copyLink}
                                            className="btn border border-surface-200 gap-1.5 text-sm">
                                            {linkCopied ? <><Check size={14} /> Copiado!</> : <><Copy size={14} /> Copiar</>}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose}
                            className="btn flex-1 border border-surface-200 hover:bg-surface-50">Cancelar</button>
                        <button type="submit" disabled={isPending}
                            className="btn btn-primary flex-1 gap-2">
                            {isPending ? <Loader2 size={16} className="animate-spin" /> : service ? 'Salvar' : 'Criar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

/* ─── Planos Manager ─── */
function PlanosManager({ servicoId, planos: initialPlanos }: { servicoId: string; planos: ServicoPlano[] }) {
    const upsertMut = useUpsertPlano()
    const deleteMut = useDeletePlano()
    const [expanded, setExpanded] = useState(false)
    const [adding, setAdding] = useState(false)
    const [editPlano, setEditPlano] = useState<Partial<ServicoPlano> | null>(null)

    // Local state so items persist even though parent's `editing` object is stale
    const [localPlanos, setLocalPlanos] = useState<ServicoPlano[]>(initialPlanos)

    const [nome, setNome] = useState('')
    const [descricao, setDescricao] = useState('')
    const [valor, setValor] = useState('')
    const [valorAdesao, setValorAdesao] = useState('')
    const [destaque, setDestaque] = useState(false)
    const [inclusoes, setInclusoes] = useState('')
    const [saveError, setSaveError] = useState('')

    const resetForm = () => {
        setNome(''); setDescricao(''); setValor(''); setValorAdesao(''); setDestaque(false); setInclusoes('')
        setAdding(false); setEditPlano(null); setSaveError('')
    }

    const startEdit = (p: ServicoPlano) => {
        setEditPlano(p)
        setNome(p.nome)
        setDescricao(p.descricao || '')
        setValor(String(p.valor_mensal))
        setValorAdesao(p.valor_adesao != null ? String(p.valor_adesao) : '')
        setDestaque(p.destaque)
        setInclusoes((p.inclusoes || []).map(i => i.titulo).join('\n'))
        setAdding(true)
        setSaveError('')
    }

    const handleSave = async () => {
        setSaveError('')
        try {
            const incArray = inclusoes.split('\n').filter(l => l.trim()).map(l => ({ titulo: l.trim() }))
            const saved = await upsertMut.mutateAsync({
                ...(editPlano?.id ? { id: editPlano.id } : {}),
                servico_id: servicoId,
                nome,
                descricao: descricao || null,
                valor_mensal: parseFloat(valor),
                valor_adesao: valorAdesao ? parseFloat(valorAdesao) : null,
                destaque,
                inclusoes: incArray,
                ordem: editPlano?.ordem ?? localPlanos.length,
            })
            // Update local state with the saved item
            if (editPlano?.id) {
                setLocalPlanos(prev => prev.map(p => p.id === saved.id ? saved : p))
            } else {
                setLocalPlanos(prev => [...prev, saved])
            }
            resetForm()
        } catch (err: any) {
            console.error('Erro ao salvar plano:', err)
            setSaveError(err.message || 'Erro ao salvar plano.')
        }
    }

    const handleDelete = async (id: string) => {
        try {
            await deleteMut.mutateAsync(id)
            setLocalPlanos(prev => prev.filter(p => p.id !== id))
        } catch (err: any) {
            console.error('Erro ao excluir plano:', err)
        }
    }

    return (
        <div className="space-y-3 p-4 rounded-2xl bg-indigo-50/40 border border-indigo-100">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
                <div className="flex items-center gap-2">
                    <Layers size={16} className="text-indigo-600" />
                    <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">
                        Multiplanos ({localPlanos.length})
                    </p>
                </div>
                {expanded ? <ChevronUp size={16} className="text-indigo-400" /> : <ChevronDown size={16} className="text-indigo-400" />}
            </div>

            {expanded && (
                <div className="space-y-2">
                    {localPlanos.length === 0 && !adding && (
                        <p className="text-xs text-surface-400 italic">Nenhum plano configurado. O serviço usa valor único.</p>
                    )}
                    {localPlanos.map(p => (
                        <div key={p.id} className={`p-3 rounded-xl bg-white border ${p.destaque ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-indigo-100'} flex items-center justify-between`}>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <p className="text-sm font-bold text-surface-900">{p.nome}</p>
                                    {p.destaque && <span className="text-[9px] font-bold text-indigo-600 bg-indigo-100 px-1.5 py-0.5 rounded-full">DESTAQUE</span>}
                                </div>
                                <p className="text-xs text-surface-400 mt-0.5">{formatCurrency(p.valor_mensal)}/mês • {(p.inclusoes || []).length} inclusões</p>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                                <button type="button" onClick={() => startEdit(p)} className="p-1.5 rounded-lg hover:bg-indigo-50 text-surface-400 hover:text-indigo-600">
                                    <Edit size={13} />
                                </button>
                                <button type="button" onClick={() => handleDelete(p.id)} className="p-1.5 rounded-lg hover:bg-danger-50 text-surface-400 hover:text-danger-600">
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        </div>
                    ))}

                    {adding ? (
                        <div className="p-3 rounded-xl bg-white border border-indigo-200 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] font-bold text-surface-500 mb-0.5">Nome do Plano</label>
                                    <input type="text" value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Platinum" className="input text-sm" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-surface-500 mb-0.5">Valor Mensal (R$)</label>
                                    <input type="number" step="0.01" min="0" value={valor} onChange={e => setValor(e.target.value)} placeholder="149.90" className="input text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-surface-500 mb-0.5">Descrição</label>
                                <input type="text" value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="O que diferencia este plano..." className="input text-sm" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] font-bold text-surface-500 mb-0.5">Valor Adesão (R$)</label>
                                    <input type="number" step="0.01" min="0" value={valorAdesao} onChange={e => setValorAdesao(e.target.value)} placeholder="Herda do serviço" className="input text-sm" />
                                </div>
                                <label className="flex items-center gap-2 cursor-pointer self-end pb-2">
                                    <input type="checkbox" checked={destaque} onChange={e => setDestaque(e.target.checked)} className="w-4 h-4 rounded border-indigo-300 text-indigo-600" />
                                    <span className="text-xs font-bold text-surface-700">Plano destaque</span>
                                </label>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-surface-500 mb-0.5">Inclusões (uma por linha)</label>
                                <textarea value={inclusoes} onChange={e => setInclusoes(e.target.value)} rows={3} placeholder={"Guincho 24h\nVidros e faróis\nAssistência completa"} className="input resize-none text-xs" />
                            </div>
                            {saveError && (
                                <div className="p-2 rounded-lg bg-danger-50 border border-danger-200">
                                    <p className="text-xs text-danger-700">{saveError}</p>
                                </div>
                            )}
                            <div className="flex gap-2">
                                <button type="button" onClick={resetForm} className="btn text-xs flex-1 border border-surface-200">Cancelar</button>
                                <button type="button" onClick={handleSave} disabled={upsertMut.isPending || !nome || !valor}
                                    className="btn btn-primary text-xs flex-1 gap-1">
                                    {upsertMut.isPending ? <Loader2 size={12} className="animate-spin" /> : editPlano ? 'Salvar' : 'Adicionar'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button type="button" onClick={() => setAdding(true)}
                            className="w-full py-2 rounded-lg border-2 border-dashed border-indigo-200 text-indigo-600 text-xs font-medium hover:bg-indigo-50 transition-colors flex items-center justify-center gap-1.5">
                            <Plus size={14} /> Adicionar Plano
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

/* ─── Adicionais Manager ─── */
function AdicionaisManager({ servicoId, adicionais: initialAdicionais }: { servicoId: string; adicionais: ServicoAdicional[] }) {
    const upsertMut = useUpsertAdicional()
    const deleteMut = useDeleteAdicional()
    const [expanded, setExpanded] = useState(false)
    const [adding, setAdding] = useState(false)
    const [editItem, setEditItem] = useState<Partial<ServicoAdicional> | null>(null)

    // Local state so items persist even though parent's `editing` object is stale
    const [localAdicionais, setLocalAdicionais] = useState<ServicoAdicional[]>(initialAdicionais)

    const [nome, setNome] = useState('')
    const [descricao, setDescricao] = useState('')
    const [tipo, setTipo] = useState<'quantidade' | 'toggle'>('quantidade')
    const [valorUnit, setValorUnit] = useState('')
    const [valorAdesaoAd, setValorAdesaoAd] = useState('')
    const [maxQtd, setMaxQtd] = useState('5')
    const [incluso, setIncluso] = useState('0')
    const [saveError, setSaveError] = useState('')

    const resetForm = () => {
        setNome(''); setDescricao(''); setTipo('quantidade'); setValorUnit(''); setValorAdesaoAd(''); setMaxQtd('5'); setIncluso('0')
        setAdding(false); setEditItem(null); setSaveError('')
    }

    const startEdit = (a: ServicoAdicional) => {
        setEditItem(a); setNome(a.nome); setDescricao(a.descricao || ''); setTipo(a.tipo as any)
        setValorUnit(String(a.valor_unitario)); setValorAdesaoAd(a.valor_adesao != null ? String(a.valor_adesao) : '')
        setMaxQtd(String(a.max_quantidade)); setIncluso(String(a.incluso_no_plano))
        setAdding(true); setSaveError('')
    }

    const handleSave = async () => {
        setSaveError('')
        try {
            const saved = await upsertMut.mutateAsync({
                ...(editItem?.id ? { id: editItem.id } : {}),
                servico_id: servicoId,
                nome,
                descricao: descricao || null,
                tipo,
                valor_unitario: parseFloat(valorUnit) || 0,
                valor_adesao: valorAdesaoAd ? parseFloat(valorAdesaoAd) : null,
                min_quantidade: 0,
                max_quantidade: parseInt(maxQtd) || 1,
                incluso_no_plano: parseInt(incluso) || 0,
                ordem: editItem?.ordem ?? localAdicionais.length,
            })
            // Update local state with the saved item
            if (editItem?.id) {
                setLocalAdicionais(prev => prev.map(a => a.id === saved.id ? saved : a))
            } else {
                setLocalAdicionais(prev => [...prev, saved])
            }
            resetForm()
        } catch (err: any) {
            console.error('Erro ao salvar adicional:', err)
            setSaveError(err.message || 'Erro ao salvar adicional.')
        }
    }

    const handleDelete = async (id: string) => {
        try {
            await deleteMut.mutateAsync(id)
            setLocalAdicionais(prev => prev.filter(a => a.id !== id))
        } catch (err: any) {
            console.error('Erro ao excluir adicional:', err)
        }
    }

    return (
        <div className="space-y-3 p-4 rounded-2xl bg-teal-50/40 border border-teal-100">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
                <div className="flex items-center gap-2">
                    <ListPlus size={16} className="text-teal-600" />
                    <p className="text-xs font-semibold text-teal-700 uppercase tracking-wide">
                        Adicionais / Dependentes ({localAdicionais.length})
                    </p>
                </div>
                {expanded ? <ChevronUp size={16} className="text-teal-400" /> : <ChevronDown size={16} className="text-teal-400" />}
            </div>

            {expanded && (
                <div className="space-y-2">
                    {localAdicionais.length === 0 && !adding && (
                        <p className="text-xs text-surface-400 italic">Nenhum adicional configurado.</p>
                    )}
                    {localAdicionais.map(a => (
                        <div key={a.id} className="p-3 rounded-xl bg-white border border-teal-100 flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-surface-900">{a.nome}</p>
                                <p className="text-xs text-surface-400 mt-0.5">
                                    {a.tipo === 'toggle' ? 'Ativa/Desativa' : `Até ${a.max_quantidade}x`}
                                    {a.incluso_no_plano > 0 ? ` • ${a.incluso_no_plano} incluso(s)` : ''}
                                    {' • '}{formatCurrency(a.valor_unitario)}/mês
                                    {a.valor_adesao != null && a.valor_adesao > 0 ? ` • Adesão ${formatCurrency(a.valor_adesao)}/un` : ''}
                                </p>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                                <button type="button" onClick={() => startEdit(a)} className="p-1.5 rounded-lg hover:bg-teal-50 text-surface-400 hover:text-teal-600">
                                    <Edit size={13} />
                                </button>
                                <button type="button" onClick={() => handleDelete(a.id)} className="p-1.5 rounded-lg hover:bg-danger-50 text-surface-400 hover:text-danger-600">
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        </div>
                    ))}

                    {adding ? (
                        <div className="p-3 rounded-xl bg-white border border-teal-200 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] font-bold text-surface-500 mb-0.5">Nome</label>
                                    <input type="text" value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Dependente" className="input text-sm" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-surface-500 mb-0.5">Tipo</label>
                                    <select value={tipo} onChange={e => setTipo(e.target.value as any)} className="input text-sm">
                                        <option value="quantidade">Quantidade (stepper)</option>
                                        <option value="toggle">Liga/Desliga</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-surface-500 mb-0.5">Descrição</label>
                                <input type="text" value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Opcional..." className="input text-sm" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] font-bold text-surface-500 mb-0.5">Valor/Mês (R$)</label>
                                    <input type="number" step="0.01" min="0" value={valorUnit} onChange={e => setValorUnit(e.target.value)} placeholder="15.00" className="input text-sm" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-surface-500 mb-0.5">Adesão/Un (R$)</label>
                                    <input type="number" step="0.01" min="0" value={valorAdesaoAd} onChange={e => setValorAdesaoAd(e.target.value)} placeholder="0 = sem adesão" className="input text-sm" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] font-bold text-surface-500 mb-0.5">Max. Qtd</label>
                                    <input type="number" min="1" value={maxQtd} onChange={e => setMaxQtd(e.target.value)} placeholder="5" className="input text-sm" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-surface-500 mb-0.5">Inclusos</label>
                                    <input type="number" min="0" value={incluso} onChange={e => setIncluso(e.target.value)} placeholder="0" className="input text-sm" />
                                    <p className="text-[9px] text-surface-300 mt-0.5">Sem custo extra</p>
                                </div>
                            </div>
                            {saveError && (
                                <div className="p-2 rounded-lg bg-danger-50 border border-danger-200">
                                    <p className="text-xs text-danger-700">{saveError}</p>
                                </div>
                            )}
                            <div className="flex gap-2">
                                <button type="button" onClick={resetForm} className="btn text-xs flex-1 border border-surface-200">Cancelar</button>
                                <button type="button" onClick={handleSave} disabled={upsertMut.isPending || !nome}
                                    className="btn btn-primary text-xs flex-1 gap-1">
                                    {upsertMut.isPending ? <Loader2 size={12} className="animate-spin" /> : editItem ? 'Salvar' : 'Adicionar'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button type="button" onClick={() => setAdding(true)}
                            className="w-full py-2 rounded-lg border-2 border-dashed border-teal-200 text-teal-600 text-xs font-medium hover:bg-teal-50 transition-colors flex items-center justify-center gap-1.5">
                            <Plus size={14} /> Adicionar Item
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

/* ─── Cross-Sell Section ─── */
function CrossSellSection({ allServicos, crossSellState, setCrossSellState }: {
    crossSell: CrossSellConfig | null
    allServicos: Servico[]
    onChange: (cs: CrossSellConfig) => void
    crossSellState: CrossSellConfig
    setCrossSellState: (cs: CrossSellConfig) => void
}) {
    const addItem = () => {
        setCrossSellState({
            ...crossSellState,
            servicos: [...crossSellState.servicos, {
                servico_id: '',
                desconto_percentual: 10,
                desconto_fixo: null,
                titulo_oferta: 'Adicione por apenas',
                destaque: false
            }]
        })
    }

    const updateItem = (idx: number, updates: Partial<CrossSellItem>) => {
        const newServicos = [...crossSellState.servicos]
        newServicos[idx] = { ...newServicos[idx]!, ...updates }
        setCrossSellState({ ...crossSellState, servicos: newServicos })
    }

    const removeItem = (idx: number) => {
        setCrossSellState({
            ...crossSellState,
            servicos: crossSellState.servicos.filter((_, i) => i !== idx)
        })
    }

    return (
        <div className="space-y-3 p-4 rounded-2xl bg-cyan-50/40 border border-cyan-100">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <ShoppingBag size={16} className="text-cyan-600" />
                    <p className="text-xs font-semibold text-cyan-700 uppercase tracking-wide">Cross-sell (Ofertas no Checkout)</p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={crossSellState.habilitado}
                        onChange={(e) => setCrossSellState({ ...crossSellState, habilitado: e.target.checked })}
                        className="w-4 h-4 rounded border-surface-300 text-cyan-600" />
                    <span className="text-xs text-surface-700">Ativar</span>
                </label>
            </div>

            {crossSellState.habilitado && (
                <div className="space-y-3">
                    {crossSellState.servicos.map((item, idx) => (
                        <div key={idx} className="p-3 rounded-xl bg-white border border-cyan-100 space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-medium text-cyan-700">Oferta {idx + 1}</span>
                                <button type="button" onClick={() => removeItem(idx)}
                                    className="p-1 rounded hover:bg-danger-50 text-danger-600">
                                    <X size={12} />
                                </button>
                            </div>
                            <select value={item.servico_id}
                                onChange={(e) => updateItem(idx, { servico_id: e.target.value })}
                                className="input text-sm">
                                <option value="">Selecione o serviço...</option>
                                {allServicos.filter(s => s.contratacao_online && s.ativo).map(s => (
                                    <option key={s.id} value={s.id}>{s.nome} — {formatCurrency(s.valor_mensal)}/mês</option>
                                ))}
                            </select>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] font-bold text-surface-500 mb-0.5">Desconto (%)</label>
                                    <input type="number" step="1" min="0" max="100"
                                        value={item.desconto_percentual || ''}
                                        onChange={(e) => updateItem(idx, { desconto_percentual: parseFloat(e.target.value) || null, desconto_fixo: null })}
                                        placeholder="10" className="input text-sm" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-surface-500 mb-0.5">ou Fixo (R$)</label>
                                    <input type="number" step="0.01" min="0"
                                        value={item.desconto_fixo || ''}
                                        onChange={(e) => updateItem(idx, { desconto_fixo: parseFloat(e.target.value) || null, desconto_percentual: null })}
                                        placeholder="5.00" className="input text-sm" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-surface-500 mb-0.5">Texto da oferta</label>
                                <input type="text" value={item.titulo_oferta}
                                    onChange={(e) => updateItem(idx, { titulo_oferta: e.target.value })}
                                    placeholder="Adicione por apenas" className="input text-sm" />
                            </div>
                        </div>
                    ))}
                    <button type="button" onClick={addItem}
                        className="w-full py-2 rounded-lg border-2 border-dashed border-cyan-200 text-cyan-600 text-xs font-medium hover:bg-cyan-50 transition-colors flex items-center justify-center gap-1.5">
                        <Plus size={14} /> Adicionar Oferta de Cross-sell
                    </button>
                </div>
            )}
        </div>
    )
}
